import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

/* Wallet-by-wallet tables are fine for a member with three wallets and a
   never-ending scroll for a community with 150. So: big lists start closed, the
   choice is remembered per table, and the totals stay on screen either way. */

const KEY = (k) => `collapse:${k}`;

export function useCollapse(storageKey, rowCount, threshold = 12) {
  // `null` = not decided yet. The row count arrives after the data does, so the
  // default can't be worked out on first render.
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (open !== null || !rowCount) return;
    let saved = null;
    try { saved = window.localStorage.getItem(KEY(storageKey)); } catch { /* private mode */ }
    setOpen(saved ? saved === "open" : rowCount <= threshold);
  }, [rowCount, open, storageKey, threshold]);

  const toggle = () => setOpen((o) => {
    const next = !o;
    try { window.localStorage.setItem(KEY(storageKey), next ? "open" : "shut"); } catch { /* ignore */ }
    return next;
  });

  return { open: open === true, decided: open !== null, toggle };
}

export function CollapseToggle({ open, onClick, count, noun = "wallets", testId }) {
  const Icon = open ? ChevronDown : ChevronRight;
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      aria-expanded={open}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[12px] font-semibold
                 border border-black/10 bg-white/70 hover:border-black/25 transition-colors">
      <Icon size={14} />
      {open ? "Hide" : "Show"} {count} {count === 1 ? noun.replace(/s$/, "") : noun}
    </button>
  );
}

export function CollapsedNote({ count, noun = "wallets", children, testId }) {
  return (
    <div className="px-5 py-3 text-[12px] text-ink-3 flex items-center gap-2 flex-wrap" data-testid={testId}>
      <span>
        {count} {count === 1 ? noun.replace(/s$/, "") : noun} hidden to keep this page short
        {children ? <> — {children}</> : null}
      </span>
    </div>
  );
}

export default useCollapse;
